import type { MountableApp } from '@opengov/psp-contract';
import type { AppConfig, ResolvedRouteRegistryEntry } from '../types';

/** The part of a resolved app or route registry entry that locates its modules. */
type RegistryModuleRoutes = Pick<AppConfig | ResolvedRouteRegistryEntry, 'id' | 'moduleRoutes'>;

/**
 * A module that sign-out must reach: an app, and the registry route its module was mounted from.
 * The route matters because one app can serve a different module per route, and the session
 * belongs to the module that mounted.
 */
export interface SessionEndModule {
  appId: string;
  routePattern: string;
}

/** A remembered module, with the value its key held when read. */
export interface RememberedSessionEnd extends SessionEndModule {
  /** The URL the module was loaded from when it mounted. Compared at sign-out, never imported. */
  mountedUrl: string;
  value: string;
  /** When the module last mounted, or `undefined` for a value the shell didn't write in its shape. */
  rememberedAt: number | undefined;
}

/**
 * Modules that opted in to `sessionEndWhenUnmounted`, have mounted in this browser, and have not
 * had their session ended since. Kept in localStorage rather than memory because the session the
 * module ends outlives the page: a reload, or another tab, must still find it at sign-out.
 *
 * One key per module, so tabs remembering and forgetting different modules at once never
 * overwrite each other's entries. Each remember writes a fresh value, holding when the module
 * mounted, and sign-out removes a key only if it still holds the value read when sign-out began,
 * so a module remembered again meanwhile (another tab mounting it) stays remembered.
 *
 * The key holds the app id, the route pattern, and the URL the module mounted from, so an app that
 * loads a different module per entity has each entity's module remembered separately. At sign-out
 * the module URL is resolved from the registries as the code defines them, before dev-tool
 * overrides, and the module is loaded only when that URL equals the mounted one. So no value in
 * this origin's storage can choose which code the shell imports: the stored URL is compared,
 * never imported.
 */
export const SESSION_END_KEY_PREFIX = 'psp.sessionEnd.';

type KeyedModule = SessionEndModule & { mountedUrl: string };

const keyFor = ({ appId, routePattern, mountedUrl }: KeyedModule) =>
  `${SESSION_END_KEY_PREFIX}${JSON.stringify([appId, routePattern, mountedUrl])}`;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value !== '';

/** The module a key names, or `undefined` for a key the shell didn't write in this shape. */
function moduleFromKey(key: string): KeyedModule | undefined {
  if (!key.startsWith(SESSION_END_KEY_PREFIX)) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(key.slice(SESSION_END_KEY_PREFIX.length));
  } catch {
    return undefined;
  }
  if (!Array.isArray(parsed) || parsed.length !== 3) return undefined;
  const [appId, routePattern, mountedUrl]: unknown[] = parsed;
  if (
    !isNonEmptyString(appId) ||
    !isNonEmptyString(routePattern) ||
    !isNonEmptyString(mountedUrl)
  ) {
    return undefined;
  }
  return { appId, routePattern, mountedUrl };
}

/** When a value says its module mounted, or `undefined` for a value not in the shape written. */
function rememberedAtFrom(value: string): number | undefined {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return undefined;
  }
  if (typeof parsed !== 'object' || parsed === null || !('rememberedAt' in parsed)) {
    return undefined;
  }
  const { rememberedAt } = parsed;
  return typeof rememberedAt === 'number' && Number.isFinite(rememberedAt)
    ? rememberedAt
    : undefined;
}

/**
 * How long after a module last mounted sign-out keeps trying to end its session, as documented on
 * `MountableApp.sessionEndWhenUnmounted`. It matches the longest refresh-token lifetime of the
 * modules that opt in, so past it there is no session left to end. Until then an entry no
 * sign-out can end right now is kept: its route may resolve to the module that mounted again,
 * under the entity it mounted for or once a flag-gated app is back in the registries.
 */
export const SESSION_END_MAX_AGE_DAYS = 30;

const SESSION_END_MAX_AGE_MS = SESSION_END_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;

/** Whether sign-out should stop trying to end this module's session, as of `now`. */
export function isPastSessionEndMaxAge(entry: RememberedSessionEnd, now: number): boolean {
  return entry.rememberedAt === undefined || now - entry.rememberedAt > SESSION_END_MAX_AGE_MS;
}

type SessionEndingModule = MountableApp & Required<Pick<MountableApp, 'onSessionEnd'>>;

/** Whether the shell must end this module's session at sign-out even when it is not mounted. */
export function endsSessionWhenUnmounted(module: MountableApp): module is SessionEndingModule {
  return module.sessionEndWhenUnmounted === true && typeof module.onSessionEnd === 'function';
}

/** The remembered modules, in key order. Throws when storage is unavailable. */
export function rememberedSessionEnds(): RememberedSessionEnd[] {
  const { localStorage } = window;
  const remembered: Array<{ key: string; entry: RememberedSessionEnd }> = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key === null) continue;
    const module = moduleFromKey(key);
    if (!module) continue;
    const value = localStorage.getItem(key);
    if (value !== null) {
      remembered.push({ key, entry: { ...module, value, rememberedAt: rememberedAtFrom(value) } });
    }
  }
  return remembered.sort((a, b) => a.key.localeCompare(b.key)).map(({ entry }) => entry);
}

/**
 * Remembers that this module, mounted from `mountedUrl`, must end its session at sign-out. Throws
 * when storage is unavailable.
 */
export function rememberSessionEnd(module: SessionEndModule, mountedUrl: string): void {
  window.localStorage.setItem(
    keyFor({ ...module, mountedUrl }),
    JSON.stringify({ rememberedAt: Date.now(), nonce: crypto.randomUUID() })
  );
}

/**
 * Forgets the given modules, given the values read when sign-out began: those whose sessions
 * ended, and those past `SESSION_END_MAX_AGE_DAYS`. A module whose key has been rewritten since was
 * remembered again, so it stays; modules that failed within that age aren't passed in and stay
 * too, so the next sign-out tries them again. Throws when storage is unavailable.
 */
export function forgetSessionEnds(entries: readonly RememberedSessionEnd[]): void {
  entries.forEach((entry) => {
    if (window.localStorage.getItem(keyFor(entry)) === entry.value) {
      window.localStorage.removeItem(keyFor(entry));
    }
  });
}

/**
 * The URL of an app's module for one route pattern in the resolved registries, or `undefined`
 * when the app or route isn't in them or doesn't resolve to a module right now (for example a
 * flag-gated app whose flag is off for the active entity).
 */
export function moduleUrlFor(
  { appId, routePattern }: SessionEndModule,
  appRegistry: readonly RegistryModuleRoutes[],
  routeRegistry: readonly RegistryModuleRoutes[]
): string | undefined {
  for (const entry of [...appRegistry, ...routeRegistry]) {
    if (entry.id !== appId) continue;
    const url = entry.moduleRoutes?.find((route) => route.pattern === routePattern)?.resolver;
    if (url) return url;
  }
  return undefined;
}

type SessionEndModuleUrlResolver = (module: SessionEndModule) => string | undefined;

let moduleUrlResolver: SessionEndModuleUrlResolver | undefined;

/**
 * Sets how sign-out finds a remembered module's URL. The PSP context sets it each time it
 * resolves the registries and clears it when it unmounts; sign-out runs outside that context, in
 * the auth hook above it.
 */
export function setSessionEndModuleUrlResolver(
  resolver: SessionEndModuleUrlResolver | undefined
): void {
  moduleUrlResolver = resolver;
}

/**
 * The URL sign-out would load for a module, or `undefined` when there is none: before the
 * registries have resolved, or for an app outside them (global and unauthenticated shells).
 */
export function sessionEndModuleUrl(module: SessionEndModule): string | undefined {
  return moduleUrlResolver?.(module);
}
